import { z } from "zod";
import { BillboardId, DistrictId, GateId, PropertyId, finiteNumber } from "./primitives";
import { VenueKind } from "./venues/common";

export const Vec2 = z.object({ x: finiteNumber(-10_000, 10_000), z: finiteNumber(-10_000, 10_000) });
export type Vec2 = z.infer<typeof Vec2>;
export const Vec3 = z.object({
  x: finiteNumber(-10_000, 10_000),
  y: finiteNumber(-1_000, 1_000),
  z: finiteNumber(-10_000, 10_000),
});
export type Vec3 = z.infer<typeof Vec3>;

/** Axis-aligned box on the ground plane (y-up world, movement happens in x/z). */
export const Aabb = z
  .object({
    minX: finiteNumber(-10_000, 10_000),
    minZ: finiteNumber(-10_000, 10_000),
    maxX: finiteNumber(-10_000, 10_000),
    maxZ: finiteNumber(-10_000, 10_000),
  })
  .refine((b) => b.maxX > b.minX && b.maxZ > b.minZ, { message: "degenerate aabb" });
export type Aabb = z.infer<typeof Aabb>;

/** CIVIC = Townsy-run buildings (the Real Estate office): enterable, never purchasable. */
export const PropertyType = z.enum(["COMMERCIAL", "RESIDENTIAL", "CIVIC"]);
export type PropertyType = z.infer<typeof PropertyType>;

export const CommercialTemplateId = z.enum([
  "small_shop",
  "corner_shop",
  "office_block",
  "modern_midrise",
  "colorful_tower",
  "landmark_tower",
]);
export type CommercialTemplateId = z.infer<typeof CommercialTemplateId>;

/** Founder's progression (2026-10-10): plot (with tent) -> log cabin -> trailer home -> small house -> medium house -> mansion -> castle. */
export const HomeStageId = z.enum(["tent", "log_cabin", "trailer_home", "small_house", "medium_house", "mansion", "castle"]);
export type HomeStageId = z.infer<typeof HomeStageId>;
export const HOME_STAGE_ORDER: readonly HomeStageId[] = [
  "tent",
  "log_cabin",
  "trailer_home",
  "small_house",
  "medium_house",
  "mansion",
  "castle",
];

export const InteriorTemplateId = z.enum([
  "store_room",
  "home_room",
  "office_room",
  "showroom",
  // Attractions (ADR-0016): one shared room each, hosting the venue's game.
  "casino_floor",
  "race_track",
  "sports_center",
  "truck_arena",
  // Sports Park stadiums (ADR-0044): one shared room each, the stands round the field where two AI teams play.
  "football_stadium",
  "soccer_stadium",
  "baseball_stadium",
  "basketball_arena",
  "hockey_arena",
]);

/**
 * Store interior themes (founder feedback round 3, ADR-0006): the one shared store room is furnished
 * differently per business. Free cosmetic choice of the current commercial owner; layouts and colliders
 * live in @townsy/world (STORE_THEMES). "general" is the previous counter-and-shelves layout.
 */
export const StoreThemeId = z.enum([
  "general",
  "bakery",
  "cafe",
  "fitness",
  "bookstore",
  "office",
  "florist",
  "travel",
  "pet_shop",
  "tech_repair",
  "studio",
  "boutique",
  // Round 4 (founder 2026-10-11): modern presets for buyers such as software, web or crypto companies.
  "tech_office",
  "lounge",
  "gallery",
]);
export type StoreThemeId = z.infer<typeof StoreThemeId>;
export type InteriorTemplateId = z.infer<typeof InteriorTemplateId>;

export const SignKind = z.enum(["storefront", "skyline"]);
export const SignAnchor = z.object({
  kind: SignKind,
  /** Center of the sign surface in world units. */
  position: Vec3,
  /** Yaw in radians; the sign normal points along +z rotated by facing. */
  facing: finiteNumber(-Math.PI * 2, Math.PI * 2),
  width: finiteNumber(0.5, 60),
  height: finiteNumber(0.5, 60),
});
export type SignAnchor = z.infer<typeof SignAnchor>;

/**
 * Yard anchors (residential plots): yard_small (bushes, flowers, garden beds), yard_medium (swing set, slide,
 * trampoline), yard_large (pool, playground), patio (patio set, grill, hot tub), fence_line (fences), tree_spot (trees).
 * Interior anchors (home_room): floor_large (sofa, bed), floor_small (armchair, lamp, plant, side table),
 * table (dining table), rug (rugs), wall (TV, bookshelf, painting).
 */
export const DecorationAnchorType = z.enum([
  "yard_small",
  "yard_medium",
  "yard_large",
  "patio",
  "fence_line",
  "tree_spot",
  "floor_large",
  "floor_small",
  "table",
  "rug",
  "wall",
]);
export type DecorationAnchorType = z.infer<typeof DecorationAnchorType>;
export const DecorationAnchor = z.object({
  id: z.string().min(1).max(32),
  type: DecorationAnchorType,
  position: Vec2,
  allowedRotations: z.array(finiteNumber(-Math.PI * 2, Math.PI * 2)).min(1).max(8),
});
export type DecorationAnchor = z.infer<typeof DecorationAnchor>;

export const Entrance = z.object({
  /** Point on the sidewalk in front of the door where "Enter" becomes available and where exits spawn. */
  position: Vec2,
  /**
   * Yaw the actor faces after EXITING the building: TOWARD the building, so the third-person camera
   * sits on the sidewalk side and frames the storefront (founder feedback 2026-10-09).
   */
  facing: finiteNumber(-Math.PI * 2, Math.PI * 2),
});
export type Entrance = z.infer<typeof Entrance>;

/**
 * A walk-through trigger on a wall: an actor pressed against the wall within `halfWidth` of
 * `position` while moving along `direction` passes through (street door -> interior, interior
 * exit -> street). `direction` is the unit vector one walks to go THROUGH the door.
 */
export const Door = z.object({
  position: Vec2,
  direction: Vec2,
  halfWidth: finiteNumber(0.3, 5),
});
export type Door = z.infer<typeof Door>;

/**
 * Kinds of Townsy-run (CIVIC) building: the Main Street offices, the attractions (ADR-0016) and, world v6 (ADR-0044),
 * the five Sports Park stadiums (v-08..v-12), where fans watch two AI teams play. Every civic building is never for sale.
 */
export const CivicKind = z.enum([
  "real_estate_office",
  "home_builders",
  "furnishings",
  "casino",
  "race_track",
  "sports_center",
  "truck_arena",
  "football_stadium",
  "soccer_stadium",
  "baseball_stadium",
  "basketball_arena",
  "hockey_arena",
]);
export type CivicKind = z.infer<typeof CivicKind>;
/** The Sports Park stadiums' civic kinds (ADR-0044), in property order v-08..v-12. */
export const STADIUM_CIVIC_KINDS = ["football_stadium", "soccer_stadium", "baseball_stadium", "basketball_arena", "hockey_arena"] as const satisfies readonly CivicKind[];
export type StadiumCivicKind = (typeof STADIUM_CIVIC_KINDS)[number];

export const CivicInfo = z.object({
  kind: CivicKind,
  name: z.string().min(1).max(60),
  description: z.string().max(280),
});
export type CivicInfo = z.infer<typeof CivicInfo>;

export const Plot = z.object({
  id: PropertyId,
  districtId: DistrictId,
  type: PropertyType,
  address: z.string().min(1).max(80),
  /** Legal plot footprint. Nothing may be placed outside it. */
  bounds: Aabb,
  /** Footprint the building shell occupies (collider). */
  buildBounds: Aabb,
  entrance: Entrance,
  /** Walk-through door on the building's front wall (homes: the max-footprint front face). */
  door: Door,
  /** Yaw of the building front (radians). */
  facing: finiteNumber(-Math.PI * 2, Math.PI * 2),
  /** Commercial facade STYLE (roof, canopy, windows). Height comes from the acquisition stage, not the style. */
  commercialTemplate: CommercialTemplateId.optional(),
  /** Civic buildings only. */
  civic: CivicInfo.optional(),
  /** Residential only: where the "For Sale" sign stands while the plot is unowned. */
  lotSign: z.object({ position: Vec2, facing: finiteNumber(-Math.PI * 2, Math.PI * 2) }).optional(),
  interiorTemplate: InteriorTemplateId,
  signAnchors: z.array(SignAnchor).max(4),
  decorationAnchors: z.array(DecorationAnchor).max(24),
  /** Base presentation height band (0 = lowest). The live height is PropertyPublic.heightStage. Never implies traffic. */
  heightBand: z.number().int().min(0).max(5),
});
export type Plot = z.infer<typeof Plot>;

export const Billboard = z.object({
  id: BillboardId,
  districtId: DistrictId,
  position: Vec3,
  facing: finiteNumber(-Math.PI * 2, Math.PI * 2),
  width: finiteNumber(1, 60),
  height: finiteNumber(1, 60),
});
export type Billboard = z.infer<typeof Billboard>;

/**
 * World v5 (ADR-0041): the district a street surface, prop or collider belongs to. Items from before v5 carry none and
 * belong to the manifest's FIRST district (Central), so every v4 item keeps its exact v4 shape; every item added in v5
 * or v6 is tagged. World v6 (ADR-0042): the town is one street room, but every item still belongs to one district
 * (the HUD, map and directory work by district). Use @townsy/world `districtManifest(m, id)` rather than reading this
 * directly.
 */
const ItemDistrictId = DistrictId.optional();

export const StaticCollider = z.object({
  id: z.string().min(1).max(48),
  kind: z.enum(["building", "prop", "boundary", "billboard"]),
  aabb: Aabb,
  districtId: ItemDistrictId,
});
export type StaticCollider = z.infer<typeof StaticCollider>;

export const SurfaceKind = z.enum(["road", "sidewalk", "plaza", "grass", "crosswalk"]);
export const Surface = z.object({ kind: SurfaceKind, aabb: Aabb, districtId: ItemDistrictId });
export type Surface = z.infer<typeof Surface>;

export const PropKind = z.enum(["tree", "bench", "lamp", "planter", "fountain"]);
export const WorldProp = z.object({
  kind: PropKind,
  position: Vec2,
  rotation: finiteNumber(-Math.PI * 2, Math.PI * 2),
  scale: finiteNumber(0.25, 4).default(1),
  districtId: ItemDistrictId,
});
export type WorldProp = z.infer<typeof WorldProp>;

export const SpawnAnchor = z.object({ position: Vec2, facing: finiteNumber(-Math.PI * 2, Math.PI * 2) });
export type SpawnAnchor = z.infer<typeof SpawnAnchor>;

/**
 * A walk-only gate in a district's boundary wall (world v5, ADR-0041), paired with a gate of another district
 * (`toDistrictId` / `toGateId`; the pair points back). Walking into `door` (the walk-through door code) or the explicit
 * `cross_gate` interaction moved the visitor to the other district's street room, which placed them at the paired
 * gate's `entrance`. Cars never crossed.
 *
 * World v6 (ADR-0042, one continuous town): RETIRED. The districts are attached by road and every district's `gates`
 * is empty. The schema stays, unchanged, so a v5 manifest still parses and the realtime / browser gate code compiles
 * until ADR-0042 slices 2-4 remove it; nothing in a v6 manifest uses it.
 */
export const DistrictGate = z.object({
  id: GateId,
  toDistrictId: DistrictId,
  toGateId: GateId,
  /** The walk-through trigger on the boundary wall; `direction` points out of this district. */
  door: Door,
  /**
   * Where a visitor arriving THROUGH the paired gate appears: just inside this gate, facing INTO the district (unlike
   * a plot's entrance, which faces the building), outside the door trigger.
   */
  entrance: SpawnAnchor,
  /** The gate's name on its sign and in prompts, e.g. "Gate to Riverside". */
  label: z.string().min(1).max(60),
});
export type DistrictGate = z.infer<typeof DistrictGate>;

export const District = z.object({
  id: DistrictId,
  name: z.string().min(1).max(60),
  bounds: Aabb,
  /** Where a visitor arriving without a door appears: on the district's square (Central: the plaza), facing the shops. */
  spawn: SpawnAnchor,
  /** Extra guaranteed-free points near the plaza for recovery / overflow spawns. */
  returnAnchors: z.array(SpawnAnchor).min(1).max(16),
  /** Default overview camera framing (orbit camera looking at target): each district frames its own signs. */
  overviewCamera: z.object({
    target: Vec3,
    alpha: finiteNumber(-Math.PI * 2, Math.PI * 2),
    beta: finiteNumber(0, Math.PI),
    radius: finiteNumber(1, 1000),
  }),
  /**
   * World v5 (ADR-0041): the district's square (Central: the plaza south of Main Street), where `spawn`,
   * `returnAnchors` and "Return to plaza" are. Optional so a pre-v5 manifest still parses.
   */
  square: Aabb.optional(),
  /**
   * World v5 (ADR-0041): the walk-only gates to other districts (none in a one-district world). World v6 (ADR-0042):
   * always empty, the districts are attached by road (DistrictGate is retired).
   */
  gates: z.array(DistrictGate).max(8).default([]),
});
export type District = z.infer<typeof District>;

export const MovementConfig = z.object({
  simulationHz: z.number().int().min(5).max(60),
  patchHz: z.number().int().min(5).max(60),
  walkSpeedMetresPerSecond: finiteNumber(0.5, 10),
  actorRadiusMetres: finiteNumber(0.1, 2),
  entranceRadiusMetres: finiteNumber(0.5, 10),
  /** Max inputs the server will process per actor per tick. */
  maxInputsPerTick: z.number().int().min(1).max(10),
});
export type MovementConfig = z.infer<typeof MovementConfig>;

export const BoardAnchor = z.object({
  /** Center of the board surface (interior local coordinates). */
  position: Vec3,
  facing: finiteNumber(-Math.PI * 2, Math.PI * 2),
  width: finiteNumber(0.5, 20),
  height: finiteNumber(0.5, 10),
  /** Presentation style hint for the renderer. */
  style: z.enum(["tv", "whiteboard", "listings"]),
});
export type BoardAnchor = z.infer<typeof BoardAnchor>;

export const InteriorTemplate = z.object({
  id: InteriorTemplateId,
  /** Room floor bounds (local coordinates, centered at origin). */
  bounds: Aabb,
  spawn: SpawnAnchor,
  /** Exit mat / exit button zone. */
  exit: Entrance,
  /** Walking through this door (pressed against the front wall, moving outward) leaves the building. */
  exitDoor: Door,
  signAnchor: SignAnchor,
  /** Owner-editable marketing board (stores) or the plot listings board (Real Estate office). */
  boardAnchor: BoardAnchor.optional(),
  /** Furniture anchors (home_room only): owners place purchased furniture here. */
  decorationAnchors: z.array(DecorationAnchor).max(24).optional(),
  /** Attractions only (ADR-0016): the game this room hosts. */
  venue: VenueKind.optional(),
  colliders: z.array(StaticCollider).max(256),
});
export type InteriorTemplate = z.infer<typeof InteriorTemplate>;

export const WorldManifest = z.object({
  version: z.number().int().min(1),
  worldId: z.string().min(1).max(40),
  districts: z.array(District).min(1),
  plots: z.array(Plot).min(1),
  billboards: z.array(Billboard),
  surfaces: z.array(Surface),
  props: z.array(WorldProp),
  colliders: z.array(StaticCollider),
  interiors: z.array(InteriorTemplate).min(1),
  movement: MovementConfig,
});
export type WorldManifest = z.infer<typeof WorldManifest>;
